<template>
  <header class="dynamic-header" :class="{ 'is-island': isIslandMode, 'is-docked': !isIslandMode }">
    <div class="header-inner">
      <!-- Logo -->
      <div class="logo" @click="goHome">
        <div class="logo-icon-wrapper">
          <img src="@/assets/logo.png" alt="青帧智维科技" class="logo-img" />
        </div>
        <div class="logo-text-wrapper">
          <span class="logo-text">青帧<span>智维科技</span></span>
        </div>
      </div>

      <!-- 导航菜单 -->
      <nav class="nav-wrapper">
        <el-menu :default-active="activeMenu" mode="horizontal" class="nav-menu" @select="handleMenuSelect"
          :ellipsis="false">
          <el-menu-item index="1" @click="goHome">首页</el-menu-item>

          <!-- 产品中心 - 超宽下拉 -->
          <el-sub-menu index="2" popper-class="product-mega-popper"
            :popper-options="{ modifiers: [{ name: 'computeStyles', options: { adaptive: false } }] }">
            <template #title><span @click="goToProducts">产品中心</span></template>
            <div class="mega-menu-container product-mega-new" @mouseenter="handleSubMenuOpen('products')"
              @mouseleave="handleSubMenuClose">
              <div class="product-panel-grid">
                <div v-for="(group, groupIndex) in displayedProductCategories" :key="group.title"
                  class="product-panel-group" @mouseenter="hoveredProductGroup = groupIndex"
                  @mouseleave="hoveredProductGroup = null; activeProductItem = null">

                  <!-- 左侧：产品名称列表 -->
                  <div class="product-panel-left">
                    <div class="group-title">{{ group.title }}</div>

                    <!-- 加载中骨架屏 -->
                    <template v-if="navStore.loading">
                      <div v-for="i in 3" :key="i" class="skeleton-line"></div>
                    </template>
                    <!-- 正常产品列表 -->
                    <template v-else>
                      <div v-if="group.items.length > 0" class="product-list">
                        <div v-for="item in group.items" :key="item.name" class="product-list-item"
                          :class="{ active: hoveredProductGroup === groupIndex && activeProductItem === item.name }"
                          @mouseenter="activeProductItem = item.name" @click="navigateToProduct(item)">
                          <el-icon v-if="item.icon">
                            <component :is="item.icon" />
                          </el-icon>
                          <span>{{ item.name }}</span>
                        </div>
                      </div>
                      <div v-else class="empty-tip">暂无产品</div>
                    </template>
                  </div>

                  <!-- 右侧：预览卡片 -->
                  <div class="product-panel-right">
                    <!-- 加载中占位卡片 -->
                    <template v-if="navStore.loading">
                      <div class="preview-card skeleton-card">
                        <div class="preview-image-wrapper skeleton-box"></div>
                        <div class="preview-info">
                          <div class="skeleton-line short"></div>
                          <div class="skeleton-line long"></div>
                        </div>
                      </div>
                    </template>
                    <!-- 真实预览 -->
                    <template v-else>
                      <div class="preview-card" @click="navigateToProduct(getActiveProduct(groupIndex))">
                        <div class="preview-image-wrapper">
                          <img
                            :src="getProductImage(groupIndex, hoveredProductGroup === groupIndex ? activeProductItem : null)"
                            alt="" class="preview-img" />
                          <div class="preview-mask">
                            <span>{{ hoveredProductGroup === groupIndex && activeProductItem ? '查看详情 →' : group.title }}</span>
                          </div>
                        </div>
                        <div class="preview-info">
                          <div class="preview-model">{{ getProductModel(groupIndex, activeProductItem) }}</div>
                          <div class="preview-desc">{{ getProductDesc(groupIndex, activeProductItem) }}</div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </el-sub-menu>

          <!-- 解决方案下拉菜单 -->
          <el-sub-menu index="3" popper-class="solution-mega-popper"
            :popper-options="{ modifiers: [{ name: 'computeStyles', options: { adaptive: false } }] }">
            <template #title><span @click="goToSolutions">解决方案</span></template>
            <div class="mega-menu-container" @mouseenter="handleSubMenuOpen('solutions')"
              @mouseleave="handleSubMenuClose">
              <div class="mega-menu-content solutions-grid">
                <div class="solution-card" v-for="industry in decoratedSolutions" :key="industry.id"
                  @click="navigateToIndustry(industry)">
                  <div class="solution-icon" :style="{ background: industry.bgColor || '#E3F2FD' }">
                    <el-icon :size="36"><component :is="industry.icon" /></el-icon>
                  </div>
                  <div class="solution-info">
                    <div class="solution-name">{{ industry.industryName }}</div>
                    <div class="solution-desc">{{ industry.desc || '查看该行业解决方案' }}</div>
                  </div>
                </div>
              </div>
              <div class="mega-panel-footer" @click="goToSolutions">
                <span>查看全部解决方案</span>
                <el-icon><ArrowRight /></el-icon>
              </div>
            </div>
          </el-sub-menu>

          <!-- 服务支持 -->
          <el-sub-menu index="4" popper-class="support-mega-popper"
            :popper-options="{ modifiers: [{ name: 'computeStyles', options: { adaptive: false } }] }">
            <template #title><span @click="goToWikis">服务支持</span></template>
            <div class="mega-menu-container" @mouseenter="handleSubMenuOpen('support')"
              @mouseleave="handleSubMenuClose">
              <div class="mega-menu-content support-grid">
                <div class="support-left">
                  <div class="support-item" v-for="item in supportItems" :key="item.name"
                    @click="navigateToSupport(item)">
                    <el-icon :size="28">
                      <component :is="item.icon" />
                    </el-icon>
                    <div>
                      <div class="support-item-name">{{ item.name }}</div>
                      <div class="support-item-desc">{{ item.desc }}</div>
                    </div>
                  </div>
                  <!-- 后台配置的导航项（与首页轮播同源） -->

                </div>
                <div class="support-right">
                  <div class="support-hotline">
                    <!-- <el-icon :size="36"><Email /></el-icon> -->
                    <div>
                      <div class="hotline-label">服务邮箱</div>
                      <div class="hotline-number">qingzhenjiance@163.com</div>
                    </div>
                  </div>
                  <el-button type="primary" plain round @click="openChat">邮箱咨询</el-button>
                </div>
              </div>
              <div class="mega-panel-footer" @click="goToWikis">
                <span>进入服务支持</span>
                <el-icon><ArrowRight /></el-icon>
              </div>
            </div>
          </el-sub-menu>

          <el-menu-item index="5" @click="goToAbout">关于我们</el-menu-item>
        </el-menu>
      </nav>

      <!-- 右侧操作区 -->
      <div class="header-actions">
        <div class="platform-buttons">
          <el-button class="platform-btn platform-btn-primary" @click="openScreenPlatform">
            <el-icon><Monitor /></el-icon><span>企业登录</span><span class="platform-badge">展示</span>
          </el-button>
          <el-button class="platform-btn platform-btn-gradient" @click="openManagePlatform">
            <el-icon><Grid /></el-icon><span>物联网管理</span><span class="platform-badge">IoT</span>
          </el-button>
        </div>
      </div>
    </div>
    <div v-show="!isIslandMode" class="header-line"></div>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useProductNavStore } from '@/stores/productNav'
import { resolveIcon } from '@/utils/navIcons'  // 新增 store
import { ElMessage } from 'element-plus'

import {
  CaretBottom, User, Setting, SwitchButton,
  Connection, Monitor, Grid, Cpu, Box,
  Guide as RouterIcon,
  Document, Phone, ChatDotRound, DataAnalysis,
  Files, Tickets, Guide, EditPen,
  Wifi, Share, Cloudy, More,
  OfficeBuilding, Sunny, ColdDrink, Van,
  FirstAidKit, School, ShoppingBag,
  Suitcase, Moon, Sunrise, Promotion,
  Collection, Platform, Menu,
  HomeFilled, ShoppingCart, Management,
  Edit, Watch, WarnTriangle, ArrowRight
} from '@element-plus/icons-vue'

// 行业图标映射（保留在组件中，展示逻辑归属组件）
const INDUSTRY_ICON_MAP = {
  '仓储物流': Box,
  '工业智造': Cpu,
  '光伏新能源': Sunny,
  '楼宇园区': OfficeBuilding,
  '农业畜牧': Connection,
  '水务暖通': ColdDrink,
  '智慧城市': Monitor,
  '智慧电网': DataAnalysis,
  '智慧工厂': Grid,
  '智慧金融': Management,
  '智慧零售': ShoppingBag,
  '智能家居': HomeFilled,
  '医疗健康': FirstAidKit,
  '教育科研': School,
  '能源矿产': Suitcase,
  '交通运输': Van,
  '文旅体育': Tickets,
  'default': Connection
}

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const navStore = useProductNavStore()

const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
const activeMenu = ref('1')
const isIslandMode = ref(true)
const hoveredProductGroup = ref(null)
const activeProductItem = ref(null)
let closeTimer = null
let scrollHandler = null

// ========= 数据源：使用 store 中的响应式数据 =========
const displayedProductCategories = computed(() => navStore.categories)

// 装饰后的行业列表（加上图标、颜色等展示字段）
const decoratedSolutions = computed(() => {
  const list = navStore.rawIndustries.map(item => ({
    ...item,
    icon: INDUSTRY_ICON_MAP[item.industryName] || INDUSTRY_ICON_MAP.default,
    bgColor: '#E3F2FD',
    desc: `${item.industryName}解决方案`
  }))
  // 追加“更多行业”入口
  list.push({
    id: 'more',
    industryName: '更多行业',
    icon: More,
    bgColor: '#F1F5F9',
    desc: '查看全部解决方案'
  })
  return list
})

// 静态服务支持列表
const supportItems = ref([
  { name: '文档中心', desc: '产品手册、开发指南', icon: Document },
  // { name: '视频教程', desc: '产品使用视频教学', icon: Guide },
  { name: '技术论坛', desc: '开发者技术交流社区', icon: ChatDotRound },
  // { name: '常见问题', desc: '高频问题快速解答', icon: Tickets },
  // { name: '工单系统', desc: '提交问题工单', icon: EditPen }
])

// ========= 辅助方法（图片、型号、描述） =========
const getProductImage = (groupIndex, activeItemName) => {
  const group = displayedProductCategories.value[groupIndex]
  if (!group || !group.items.length) {
    return `NOimage`
  }
  if (activeItemName) {
    const found = group.items.find(item => item.name === activeItemName)
    if (found?.image) return found.image
  }
  return group.items[0].image
}

const getProductModel = (groupIndex, activeItemName) => {
  const group = displayedProductCategories.value[groupIndex]
  if (!group || !group.items.length) return ''
  if (activeItemName) {
    const found = group.items.find(item => item.name === activeItemName)
    if (found?.model) return found.model
  }
  return group.items[0]?.model || ''
}

const getProductDesc = (groupIndex, activeItemName) => {
  const group = displayedProductCategories.value[groupIndex]
  if (!group || !group.items.length) return ''
  if (activeItemName) {
    const found = group.items.find(item => item.name === activeItemName)
    if (found?.description) return found.description
  }
  return group.items[0]?.description || ''
}

const getActiveProduct = (groupIndex) => {
  const group = displayedProductCategories.value[groupIndex]
  if (!group || !group.items.length) return null
  if (hoveredProductGroup.value === groupIndex && activeProductItem.value) {
    const found = group.items.find(item => item.name === activeProductItem.value)
    if (found) return found
  }
  return group.items[0]
}

// ========= 导航跳转 =========
const navigateToProduct = (item) => {
  if (!item) return
  router.push(`/product/${item.id}`)
}
const navigateToIndustry = (industry) => {
  if (!industry) return
  if (industry.id === 'more') {
    router.push('/solutions')
  } else {
    router.push(`/solutions?industryIds=${industry.id}`)
  }
}
const navigateToSupport = (item) => router.push(`/wikis`)

// 后台导航项跳转：优先 linkUrl，其次关联产品/方案
const navigateToNavbarItem = (nav) => {
  let url = nav.linkUrl
  if (!url && nav.productId) url = '/product/' + nav.productId
  if (!url && nav.solutionId) url = '/solution/' + nav.solutionId
  if (!url) return
  if (/^https?:\/\//i.test(url)) window.open(url, '_blank')
  else router.push(url)
}
const openChat = () => ElMessage.info('请使用邮箱联系我们...')

// ========= 下拉菜单控制 =========
const handleSubMenuOpen = () => {
  if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
}
const handleSubMenuClose = () => {
  closeTimer = setTimeout(() => {
    document.querySelectorAll('.el-sub-menu.is-opened').forEach(el => el.classList.remove('is-opened'))
    closeTimer = null
  }, 200)
}

// ========= 滚动岛模式 =========
const SCROLL_THRESHOLD = 900
const handleScroll = () => { isIslandMode.value = (window.scrollY || 0) < SCROLL_THRESHOLD }

watch(() => route.path, (newPath) => {
  if (scrollHandler) { window.removeEventListener('scroll', scrollHandler); scrollHandler = null }
  if (newPath === '/') {
    nextTick(() => { handleScroll(); scrollHandler = handleScroll; window.addEventListener('scroll', scrollHandler, { passive: true }) })
  } else { isIslandMode.value = false }
}, { immediate: true })

// ========= 路由与菜单高亮 =========
const menuRouteMap = { '1': '/', '2': '/products', '3': '/solutions', '4': '/wikis', '5': '/about' }
const routeToMenuMap = { '/': '1', '/products': '2', '/solutions': '3', '/wikis': '4', '/about': '5' }

watch(() => route.path, (newPath) => {
  if (routeToMenuMap[newPath]) { activeMenu.value = routeToMenuMap[newPath]; return }
  if (newPath.startsWith('/product/')) { activeMenu.value = '2'; return }
  if (newPath.startsWith('/solution/')) { activeMenu.value = '3'; return }
  if (newPath.startsWith('/support/')) { activeMenu.value = '4'; return }
  activeMenu.value = '1'
}, { immediate: true })

// ========= 其他功能 =========
const goHome = () => router.push('/')
const goToProducts = () => router.push('/products')
const goToSolutions = () => {
  if (route.path === '/solutions') {
    router.replace({ path: '/solutions', query: { _t: Date.now() } })
  } else {
    router.push('/solutions')
  }
}
const goToWikis = () => router.push('/wikis')
const goToAbout = () => router.push('/about')
const openScreenPlatform = () => window.open('http://www.xiaoqingchong.com:22696/login', '_blank')
const openManagePlatform = () => window.open('http://www.xiaoqingchong.com:39273/#/login', '_blank')

const handleMenuSelect = (index) => {
  activeMenu.value = index
  const path = menuRouteMap[index]
  if (path && route.path !== path) router.push(path)
}

// ========= 生命周期 =========
onMounted(() => {
  // 初始化导航数据（store 内部自动缓存）
  navStore.fetchProductNav()
  // 其余初始化
  if (!userStore.isLoggedIn) {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try { userStore.setUser(JSON.parse(userStr)) } catch (e) { console.error('解析用户信息失败', e) }
    }
  }
})

onUnmounted(() => {
  if (scrollHandler) window.removeEventListener('scroll', scrollHandler)
  if (closeTimer) clearTimeout(closeTimer)
})
</script>



<style scoped>
/* 原有样式保持不变，新增骨架屏样式 */
.skeleton-line {
  height: 16px;
  margin: 8px 0;
  background: #f0f0f0;
  border-radius: 4px;
  animation: skeleton-pulse 1.2s ease-in-out infinite;
}
.skeleton-line.short { width: 60%; }
.skeleton-line.long { width: 85%; }
.skeleton-box {
  width: 100%;
  height: 140px;
  background: #f0f0f0;
  border-radius: 8px;
  animation: skeleton-pulse 1.2s ease-in-out infinite;
}
.skeleton-card {
  opacity: 0.7;
  cursor: default;
}
@keyframes skeleton-pulse {
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
}
/* ========== 全局过渡 & 状态 ========== */
.dynamic-header {
  position: fixed;
  z-index: 1000;
  transition: top 0.7s cubic-bezier(0.16, 1, 0.3, 1), left 0.7s cubic-bezier(0.16, 1, 0.3, 1),
    right 0.7s cubic-bezier(0.16, 1, 0.3, 1), width 0.7s cubic-bezier(0.16, 1, 0.3, 1),
    height 0.7s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.7s cubic-bezier(0.16, 1, 0.3, 1),
    background 0.6s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.6s cubic-bezier(0.16, 1, 0.3, 1),
    padding 0.7s cubic-bezier(0.16, 1, 0.3, 1), backdrop-filter 0.6s ease,
    transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}

.dynamic-header.is-docked {
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 80px;
  border-radius: 0;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  padding: 0;
}

/* ---------- 悬浮岛模式 ---------- */
.dynamic-header.is-island {
  top: 32px;
  left: 50%;
  transform: translateX(-50%);
  height: 80px;
  /* ★ 固定最大宽度（比轮播栏窄，留有余量） */
  max-width: 1460px;
  /* 正常屏幕下左右各留 30px，视觉舒适 */
  width: calc(100% - 60px);
  border-radius: 40px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow: 0 16px 56px rgba(0, 0, 0, 0.08), 0 6px 20px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 0;
  margin: 0 auto;
}

/* 内部容器内边距适当减小 */
.dynamic-header.is-island .header-inner {
  padding: 0 28px;
}

.header-inner {
  max-width: 1440px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 48px;
  box-sizing: border-box;
}

/* LOGO */
.logo {
  display: flex;
  align-items: center;
  gap: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.logo-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 14px;
  overflow: hidden;
  transition: background 0.3s ease, transform 0.3s ease;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}

.logo:hover .logo-icon-wrapper {
  background: #DBEAFE;
  transform: scale(1.06);
}

.logo-text-wrapper {
  display: flex;
  align-items: baseline;
  gap: 8px;
  overflow: hidden;
  white-space: nowrap;
}

.logo-text {
  font-weight: 800;
  color: #1E293B;
  letter-spacing: -0.5px;
  font-size: 34px;
}

.logo-text span {
  color: #2563EB;
}

.logo-sub,
.logo-tag,
.user-name,
.caret-icon {
  transition: opacity 0.5s ease, max-width 0.5s ease, margin-left 0.5s ease, padding 0.5s ease;
}

.logo-hidden,
.user-hidden {
  opacity: 0;
  max-width: 0;
  overflow: hidden;
  margin-left: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.logo-sub {
  font-size: 16px;
  color: #2563EB;
  font-weight: 600;
}

.logo-tag {
  font-size: 13px;
  font-weight: 700;
  color: #2563EB;
  background: #EEF2FF;
  padding: 4px 16px;
  border-radius: 28px;
  margin-left: 6px;
}

/* 导航菜单 */
.nav-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  margin: 0 28px;
  overflow: visible;
}

.nav-menu {
  border-bottom: none !important;
  background: transparent !important;
  min-width: auto;
  overflow: visible !important;
}

.nav-menu :deep(.el-sub-menu .el-sub-menu__icon-arrow) {
  display: none !important;
}

/* 基础菜单项 */
.nav-menu :deep(.el-menu-item),
.nav-menu :deep(.el-sub-menu .el-sub-menu__title) {
  height: 60px;
  line-height: 96px;
  font-size: 18px;
  font-weight: 600;
  color: #64748B;
  padding: 0 40px;
  border-bottom: 3px solid transparent;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

/* 悬浮岛下菜单项缩小 */
.dynamic-header.is-island .nav-menu :deep(.el-menu-item),
.dynamic-header.is-island .nav-menu :deep(.el-sub-menu .el-sub-menu__title) {
  padding: 0 22px;
  font-size: 16px;
}

.nav-menu :deep(.el-menu-item:hover),
.nav-menu :deep(.el-sub-menu__title:hover) {
  color: #1E293B !important;
  background: transparent !important;
}

.nav-menu :deep(.el-menu-item.is-active) {
  color: #2563EB !important;
  font-weight: 700;
  border-bottom: none !important;
  background: transparent !important;
}

/* ========== 统一下拉面板外壳（产品 / 解决方案 / 服务支持） ========== */
:global(.product-mega-popper.el-popper),
:global(.solution-mega-popper.el-popper),
:global(.support-mega-popper.el-popper) {
  padding: 0 !important;
  border: 1px solid #E6EBF4 !important;
  border-radius: 20px !important;
  box-shadow: 0 30px 70px -28px rgba(15, 23, 42, 0.28) !important;
  overflow: hidden;
}

:global(.product-mega-popper.el-popper) {
  width: 100vw !important;
  max-width: 1400px !important;
  min-width: 900px !important;
}

:global(.solution-mega-popper.el-popper) {
  width: 780px !important;
}

:global(.support-mega-popper.el-popper) {
  width: 680px !important;
}

/* 修复解决方案/服务支持面板右侧白条：强制 popper 内所有层级撑满宽度 */
:global(.solution-mega-popper .mega-menu-container),
:global(.support-mega-popper .mega-menu-container) {
  width: 100%;
  box-sizing: border-box;
}
:global(.solution-mega-popper ul.el-menu),
:global(.support-mega-popper ul.el-menu) {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  box-sizing: border-box;
}
:global(.solution-mega-popper ul.el-menu > li),
:global(.support-mega-popper ul.el-menu > li) {
  width: 100% !important;
}
:global(.solution-mega-popper .el-popper__content),
:global(.support-mega-popper .el-popper__content) {
  width: 100% !important;
  padding: 0 !important;
}

:global(.product-mega-popper .el-popper__arrow),
:global(.solution-mega-popper .el-popper__arrow),
:global(.support-mega-popper .el-popper__arrow) {
  display: none !important;
}

.product-mega-new {
  padding: 32px 36px !important;
  width: 100%;
  max-height: 80vh !important;
  overflow-y: auto !important;
  box-sizing: border-box;
}

.product-panel-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
}

.product-panel-group {
  grid-column: span 2;
  display: flex;
  gap: 16px;
  border-radius: 14px;
  padding: 8px;
  transition: background 0.25s;
}

.product-panel-group:hover {
  background: rgba(248, 250, 252, 0.7);
}

.product-panel-left {
  flex: 1;
  width: 200px;
}

.group-title {
  font-size: 10px;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 2px solid #F1F5F9;
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.product-list-item .el-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.product-list-item:hover,
.product-list-item.active {
  background: #EEF2FF;
  color: #2563EB;
}

.empty-tip {
  font-size: 12px;
  color: #CBD5E1;
  padding: 6px 10px;
  font-style: italic;
}

.product-panel-right {
  flex: 1;
  display: flex;
  align-items: stretch;
}

.preview-card {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #F8FAFC;
  border: 1px solid #F1F5F9;
  transition: border 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.preview-card:hover {
  border-color: #DBEAFE;
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.1);
}

.preview-image-wrapper {
  position: relative;
  width: 100%;
  height: 150px;
  overflow: hidden;
  flex-shrink: 0;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.preview-card:hover .preview-img {
  transform: scale(1.06);
}

.preview-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 12px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.5));
  color: white;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.preview-card:hover .preview-mask {
  opacity: 1;
}

.preview-info {
  padding: 10px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  background: #FFFFFF;
}

.preview-model {
  font-size: 13px;
  font-weight: 600;
  color: #1E293B;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-desc {
  font-size: 12px;
  color: #64748B;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ========== 解决方案 ========== */
.solutions-grid {
  display: grid !important;
  grid-template-columns: repeat(3, 1fr) !important;
  gap: 12px !important;
  padding: 26px 30px 16px !important;
}

.solution-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  border: 1px solid transparent;
}

.solution-card:hover {
  background: #F5F8FF;
  border-color: #DBEAFE;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.08);
}

.solution-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #2563EB;
}

.solution-info {
  flex: 1;
  min-width: 0;
}

.solution-name {
  font-weight: 600;
  color: #1E293B;
  font-size: 15px;
}

.solution-desc {
  font-size: 12px;
  color: #94A3B8;
  margin-top: 2px;
}

/* ========== 服务支持 ========== */
.support-grid {
  display: grid !important;
  grid-template-columns: 1fr 220px !important;
  gap: 20px !important;
  padding: 26px 30px 16px !important;
}

.support-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
}

.support-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.support-item:hover {
  background: #F5F8FF;
  transform: translateX(4px);
}

.support-item .el-icon {
  color: #64748B;
  flex-shrink: 0;
  font-size: 24px;
}

.support-item:hover .el-icon {
  color: #2563EB;
}

.support-item-name {
  font-weight: 500;
  color: #1E293B;
  font-size: 14px;
}

.support-item-desc {
  font-size: 12px;
  color: #94A3B8;
}

.support-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 16px 20px;
  background: #F5F8FF;
  border-radius: 16px;
  border: 1px solid #E8EEFB;
}

.support-hotline {
  display: flex;
  align-items: center;
  gap: 14px;
}

.support-hotline .el-icon {
  color: #2563EB;
  font-size: 32px;
}

.hotline-label {
  font-size: 12px;
  color: #94A3B8;
}

.hotline-number {
  font-size: 14px;
  font-weight: 700;
  color: #1E293B;
}

/* 面板底部“查看全部”链接（解决方案 / 服务支持统一） */
.mega-panel-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 30px;
  padding: 13px 0 18px;
  font-size: 13px;
  font-weight: 600;
  color: #2563EB;
  cursor: pointer;
  border-top: 1px solid #F1F5F9;
  transition: color 0.25s;
}

.mega-panel-footer:hover {
  color: #1D4ED8;
}

.mega-panel-footer .el-icon {
  font-size: 14px;
  transition: transform 0.25s;
}

.mega-panel-footer:hover .el-icon {
  transform: translateX(4px);
}

/* 右侧操作区 */
.header-actions {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}

.platform-buttons {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-right: 8px;
}

.platform-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  padding: 12px 30px 12px 22px !important;
  font-size: 16px !important;
}

/* 悬浮岛下按钮基础缩小 */
.dynamic-header.is-island .platform-btn {
  padding: 10px 20px !important;
  font-size: 15px !important;
}

.platform-btn .el-icon {
  font-size: 24px;
}

.platform-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 12px;
  border-radius: 20px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.platform-btn-primary {
  background: #EEF2FF;
  color: #1E40AF;
}

.platform-btn-primary .platform-badge {
  background: rgba(37, 99, 235, 0.15);
  color: #2563EB;
}

.platform-btn-primary:hover {
  background: #2563EB;
  color: #FFFFFF;
  transform: translateY(-4px);
  box-shadow: 0 12px 36px rgba(37, 99, 235, 0.30);
}

.platform-btn-primary:hover .platform-badge {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

.platform-btn-gradient {
  background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
  color: #FFFFFF;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.30);
}

.platform-btn-gradient .platform-badge {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

.platform-btn-gradient:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.40);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 4px 20px 4px 4px;
  border-radius: 34px;
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.user-info:hover {
  background: #F1F5F9;
  border-color: #E2E8F0;
}

.user-name {
  font-size: 16px;
  color: #1E293B;
  font-weight: 600;
}

.caret-icon {
  color: #94A3B8;
  font-size: 14px;
}

.header-line {
  position: absolute;
  bottom: -1px;
  left: 20%;
  right: 20%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #2563EB, transparent);
  opacity: 0.3;
}

:deep(.el-dropdown-menu) {
  border-radius: 16px !important;
  padding: 10px !important;
  box-shadow: 0 16px 56px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #E2E8F0 !important;
}

:deep(.el-dropdown-menu .el-dropdown-item) {
  border-radius: 12px !important;
  padding: 14px 24px !important;
  transition: all 0.25s ease !important;
}

:deep(.el-dropdown-menu .el-dropdown-item:hover) {
  background: #F1F5F9 !important;
  color: #2563EB !important;
}

/* ================================
   响应式调整
   ================================ */
/* 屏幕宽度 ≤ 1300px 时，减小留白，按钮缩小 */
@media (max-width: 1300px) {
  .dynamic-header.is-island {
    width: calc(100% - 40px);
    max-width: 1200px;
  }
  .dynamic-header.is-island .header-inner {
    padding: 0 20px;
  }
  .dynamic-header.is-island .nav-menu :deep(.el-menu-item),
  .dynamic-header.is-island .nav-menu :deep(.el-sub-menu .el-sub-menu__title) {
    padding: 0 16px;
    font-size: 15px;
  }
  .dynamic-header.is-island .platform-btn {
    padding: 8px 16px !important;
    font-size: 14px !important;
  }
}

/* 1100px 以下按钮隐藏文字 */
@media (max-width: 1100px) {
  .dynamic-header.is-island {
    width: calc(100% - 30px);
    max-width: 1080px;
    border-radius: 32px;
  }
  .dynamic-header.is-island .header-inner {
    padding: 0 14px;
  }
  .dynamic-header.is-island .nav-menu :deep(.el-menu-item),
  .dynamic-header.is-island .nav-menu :deep(.el-sub-menu .el-sub-menu__title) {
    padding: 0 10px;
    font-size: 14px;
  }
  .dynamic-header.is-island .platform-btn {
    padding: 6px 10px !important;
  }
  .dynamic-header.is-island .platform-btn span:not(.el-icon) {
    display: none;
  }
  .dynamic-header.is-island .platform-btn .platform-badge {
    display: none;
  }
  .dynamic-header.is-island .logo-icon-wrapper {
    width: 44px;
    height: 44px;
  }
  .dynamic-header.is-island .logo-text {
    font-size: 22px;
  }
}

/* 900px 以下基本贴边，但保留 12px 呼吸空间 */
@media (max-width: 900px) {
  .dynamic-header.is-island {
    width: calc(100% - 24px);
    max-width: 100%;
    border-radius: 0;
    margin: 0 auto;
  }
  .dynamic-header.is-island .header-inner {
    padding: 0 8px;
  }
  .dynamic-header.is-island .nav-menu :deep(.el-menu-item),
  .dynamic-header.is-island .nav-menu :deep(.el-sub-menu .el-sub-menu__title) {
    padding: 0 6px;
    font-size: 13px;
  }
  .dynamic-header.is-island .platform-btn {
    padding: 4px 8px !important;
  }
  .dynamic-header.is-island .platform-btn .el-icon {
    font-size: 22px;
  }
  .dynamic-header.is-island .logo-icon-wrapper {
    width: 36px;
    height: 36px;
  }
  .dynamic-header.is-island .logo-text {
    font-size: 18px;
  }
}

/* 768px 以下隐藏导航，小屏整体优化 */
@media (max-width: 768px) {
  .dynamic-header.is-island .nav-wrapper {
    display: none;
  }
  .product-panel-grid {
    grid-template-columns: 1fr;
  }
  .product-panel-group {
    grid-column: span 1;
    flex-direction: column;
  }
  .preview-image-wrapper {
    height: 120px;
  }
}

/* 产品 mega 菜单微调 */
@media (max-width: 1024px) {
  .product-panel-grid {
    gap: 5px;
  }
}

/* ===== ??????? ===== */
.navbar-items-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 20px;
  max-width: 900px;
}
.navbar-item-card {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s ease;
}
.navbar-item-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.1);
  border-color: #2563EB;
}
.navbar-item-image {
  height: 120px;
  overflow: hidden;
}
.navbar-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.navbar-item-card:hover .navbar-item-image img {
  transform: scale(1.05);
}
.navbar-item-info {
  padding: 12px 14px;
}
.navbar-item-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}
.navbar-item-subtitle {
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== 数据库导航栏项 ===== */
.navbar-dynamic-item {
  border-top: 1px dashed #e5e7eb;
  padding-top: 14px;
}
.navbar-item-thumb {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
}
.navbar-dynamic-title { font-size: 13px; font-weight: 600; color: #94a3b8; margin-bottom: 6px; letter-spacing: 1px; }
.navbar-item-thumb--icon { display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg,#eef2ff,#e0e7ff); color: #4f46e5; }
.navbar-dynamic-item .support-item { margin-top: 8px; }
</style>